// HqCashLedgerPage — 本部両替金 / 本部現金 出納帳 (2026-07-28)
//   紙の手書き出納帳を全廃し FREEEMAN が唯一の正式帳簿 (SoT)。
//   v1 = 紙帳簿転記 (paper_import) の閲覧: 月フィルタ・差引残高 (連鎖計算)・紙残高との照合表示・印刷。
//   記帳フォーム/実査/freee突合は次段実装 (設計書 hq_cash_ledger_design_20260728)。

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchHqCashLedger, HqCashBook, HqCashLedgerResponse, HqCashLedgerRow } from '../api/client';

const INK = '#2b2926';
const SUB = '#736d65';

const BOOK_META: Record<HqCashBook, { title: string; walletLabel: string; accent: string }> = {
  ryogae: { title: '本部両替金出納帳', walletLabel: 'freee口座: 本部両替金 (6361178)', accent: '#2f5fd0' },
  honbu: { title: '本部現金出納帳', walletLabel: 'freee口座: 現金 (6248106)', accent: '#7a5fd0' },
};

const fmtYen = (n: number | null | undefined) =>
  n == null ? '' : `¥${Number(n).toLocaleString('ja-JP')}`;

const fmtDate = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dow = '日月火水木金土'[new Date(y, m - 1, d).getDay()];
  return `${m}/${d}(${dow})`;
};

const CAT_LABEL: Record<string, string> = {
  paper_import: '紙帳簿',
  change_handout: '釣銭渡し',
  change_return: '釣銭返却',
  coin_swap: '両替金庫⇔現金',
  bank_exchange: '銀行両替',
  other_expected: 'その他(freee対象)',
  other_memo: 'メモ',
};

export const HqCashLedgerPage: React.FC<{ book: HqCashBook }> = ({ book }) => {
  const meta = BOOK_META[book];
  const [data, setData] = useState<HqCashLedgerResponse | null>(null);
  const [month, setMonth] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (m: string) => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchHqCashLedger(book, m || undefined);
      setData(r);
      if (!m && r.months.length > 0) setMonth('all');
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [book]);

  useEffect(() => { setData(null); setMonth(''); load('all'); }, [book, load]);

  const rows = data?.rows || [];
  const yearsSpan = useMemo(() => {
    if (!data?.months?.length) return '';
    return `${data.months[0]} 〜 ${data.months[data.months.length - 1]}`;
  }, [data]);

  return (
    <div style={{ padding: '18px 24px 60px', maxWidth: 1080 }}>
      <div className="pa-no-print" style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
        <h2 style={{ margin: '4px 0 2px', fontSize: '1.25rem', color: INK }}>{meta.title}</h2>
        <span style={{ fontSize: '0.75rem', color: SUB }}>{meta.walletLabel}</span>
        <span style={{ fontSize: '0.72rem', color: SUB }}>
          紙帳簿は全廃 — この台帳が正式帳簿 (SoT)。紙転記期間: {yearsSpan}
        </span>
      </div>

      {/* 残高カード */}
      <div className="pa-no-print" style={{ display: 'flex', gap: 10, margin: '12px 0', flexWrap: 'wrap' }}>
        <div style={cardStyle}>
          <div style={cardLabel}>帳簿残高 (最終)</div>
          <div style={{ ...cardValue, color: meta.accent }}>{fmtYen(data?.final_balance)}</div>
          <div style={cardSub}>開始繰越 {fmtYen(data?.anchor?.anchor_physical_balance)} ({data?.anchor?.anchor_date || '—'})</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabel}>{month === 'all' ? '全期間 収入/支払' : `${month} 収入/支払`}</div>
          <div style={{ ...cardValue, fontSize: '1rem' }}>
            <span style={{ color: '#2e7d32' }}>{fmtYen(data?.income_total)}</span>
            {' / '}
            <span style={{ color: '#c26a1b' }}>{fmtYen(data?.expense_total)}</span>
          </div>
          <div style={cardSub}>{data?.row_count ?? 0} 行{month !== 'all' && data ? ` ・ 繰越 ${fmtYen(data.carry_in)}` : ''}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabel}>紙残高との照合</div>
          <div style={{ ...cardValue, fontSize: '1rem', color: (data?.mismatch_count || 0) > 0 ? '#c62828' : '#2e7d32' }}>
            {data ? ((data.mismatch_count || 0) > 0 ? `不一致 ${data.mismatch_count} 行` : '全行一致 ✓') : '—'}
          </div>
          <div style={cardSub}>連鎖計算残高 vs 紙の差引残高欄</div>
        </div>
      </div>

      {/* フィルタ */}
      <div className="pa-no-print" style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0 10px' }}>
        <select
          value={month}
          onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ddd9d3', fontSize: '0.8125rem' }}
        >
          <option value="all">全期間</option>
          {(data?.months || []).slice().reverse().map((m) => (
            <option key={m} value={m}>{m.replace('-', '年')}月</option>
          ))}
        </select>
        <button type="button" onClick={() => load(month || 'all')} style={btnStyle}>再読込</button>
        <button type="button" onClick={() => window.print()} style={btnStyle}>🖨 印刷</button>
        {loading && <span style={{ fontSize: '0.75rem', color: SUB }}>読込中…</span>}
        {error && <span style={{ fontSize: '0.75rem', color: '#c62828' }}>{error}</span>}
      </div>

      {/* 出納帳テーブル */}
      <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e5e1da', borderRadius: 10 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8125rem' }}>
          <thead>
            <tr style={{ background: '#faf8f5', color: SUB }}>
              <th style={th}>月日</th>
              <th style={{ ...th, textAlign: 'left' }}>摘要</th>
              <th style={{ ...th, textAlign: 'right' }}>収入</th>
              <th style={{ ...th, textAlign: 'right' }}>支払</th>
              <th style={{ ...th, textAlign: 'right' }}>差引残高</th>
              <th style={th}>種別</th>
              <th style={th}>照合</th>
            </tr>
          </thead>
          <tbody>
            {month !== 'all' && data && (
              <tr style={{ background: '#faf8f5' }}>
                <td style={td} />
                <td style={{ ...td, fontWeight: 700 }}>前月繰越</td>
                <td style={td} /><td style={td} />
                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmtYen(data.carry_in)}</td>
                <td style={td} /><td style={td} />
              </tr>
            )}
            {rows.map((r) => <LedgerRow key={r.id} r={r} />)}
            {!loading && rows.length === 0 && (
              <tr><td style={{ ...td, textAlign: 'center', color: SUB }} colSpan={7}>データなし</td></tr>
            )}
          </tbody>
          {rows.length > 0 && data && (
            <tfoot>
              <tr style={{ borderTop: '3px double #bbb5ac', fontWeight: 800 }}>
                <td style={td} />
                <td style={td}>合計</td>
                <td style={{ ...td, textAlign: 'right', color: '#2e7d32' }}>{fmtYen(data.income_total)}</td>
                <td style={{ ...td, textAlign: 'right', color: '#c26a1b' }}>{fmtYen(data.expense_total)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{fmtYen(rows[rows.length - 1]?.running_balance)}</td>
                <td style={td} /><td style={td} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div style={{ marginTop: 8, fontSize: '0.7rem', color: SUB }}>
        照合列: ✓=前行からの計算と紙の差引残高が一致 / ⚠=不一致 (差額表示・紙の算術誤記 or 転記要確認。残高は紙を正として継続) / −=紙に残高記載なし。
        低確度転記 (conf low) は摘要末尾に「?」を表示。行クリックで紙帳簿の出典 (ページ・行) を表示。
      </div>

      <style>{`
        @media print {
          .pa-no-print { display: none !important; }
          .fm-shell-main { margin-left: 0 !important; }
        }
      `}</style>
    </div>
  );
};

const LedgerRow: React.FC<{ r: HqCashLedgerRow }> = ({ r }) => {
  const [open, setOpen] = useState(false);
  const mismatch = r.paper_mismatch !== 0;
  return (
    <>
      <tr
        onClick={() => setOpen((v) => !v)}
        style={{ borderTop: '1px solid #f0ede8', cursor: 'pointer', background: mismatch ? '#fff8ec' : undefined }}
      >
        <td style={{ ...td, whiteSpace: 'nowrap', color: SUB }}>{fmtDate(r.date)}</td>
        <td style={td}>
          {r.summary || <span style={{ color: '#b5afa6' }}>(摘要なし)</span>}
          {r.conf === 'low' && <span title="判読低確度" style={{ color: '#c26a1b' }}> ?</span>}
        </td>
        <td style={{ ...td, textAlign: 'right', color: '#2e7d32', fontVariantNumeric: 'tabular-nums' }}>
          {r.direction === 'in' ? fmtYen(r.amount) : ''}
        </td>
        <td style={{ ...td, textAlign: 'right', color: '#c26a1b', fontVariantNumeric: 'tabular-nums' }}>
          {r.direction === 'out' ? fmtYen(r.amount) : ''}
        </td>
        <td style={{ ...td, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {fmtYen(r.running_balance)}
        </td>
        <td style={{ ...td, textAlign: 'center' }}>
          <span style={{
            fontSize: '0.66rem', padding: '2px 7px', borderRadius: 9,
            background: r.category === 'paper_import' ? '#f0ede8' : '#e8effc',
            color: r.category === 'paper_import' ? SUB : '#2f5fd0',
          }}>{CAT_LABEL[r.category] || r.category}</span>
        </td>
        <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
          {r.paper_balance == null ? (
            <span style={{ color: '#b5afa6' }}>−</span>
          ) : mismatch ? (
            <span
              title={`前行からの計算値 ${fmtYen((r.paper_balance ?? 0) + r.paper_mismatch)} ≠ 紙の残高 ${fmtYen(r.paper_balance)} (紙の算術誤記の可能性)`}
              style={{ color: '#c62828', fontWeight: 700 }}
            >
              ⚠ {r.paper_mismatch > 0 ? '+' : ''}{r.paper_mismatch.toLocaleString('ja-JP')}
            </span>
          ) : (
            <span style={{ color: '#2e7d32' }}>✓</span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="pa-no-print" style={{ background: '#faf9f7' }}>
          <td style={{ ...td, fontSize: '0.72rem', color: SUB }} colSpan={7}>
            紙帳簿 {r.source_doc || '—'} p{r.source_page ?? '—'} 行{r.source_line ?? '—'}
            {r.date_raw ? ` ・ 記載日付「${r.date_raw}」` : ''}
            {r.paper_balance != null ? ` ・ 紙の差引残高 ${fmtYen(r.paper_balance)}` : ''}
            {r.note ? ` ・ メモ: ${r.note}` : ''}
          </td>
        </tr>
      )}
    </>
  );
};

const btnStyle: React.CSSProperties = {
  padding: '6px 12px', borderRadius: 8, border: '1px solid #ddd9d3', background: '#fff',
  fontSize: '0.8125rem', cursor: 'pointer', color: INK,
};
const cardStyle: React.CSSProperties = {
  background: '#fff', border: '1px solid #e5e1da', borderRadius: 10, padding: '10px 16px', minWidth: 200,
};
const cardLabel: React.CSSProperties = { fontSize: '0.68rem', color: SUB, fontWeight: 700 };
const cardValue: React.CSSProperties = { fontSize: '1.25rem', fontWeight: 800, color: INK, marginTop: 2 };
const cardSub: React.CSSProperties = { fontSize: '0.68rem', color: SUB, marginTop: 2 };
const th: React.CSSProperties = { padding: '8px 10px', fontSize: '0.7rem', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '6px 10px', verticalAlign: 'top' };

export default HqCashLedgerPage;
